// «موعد جديد» — the booking side panel (HANDOFF §3 `/booking/new`, §5.1;
// boards booking/new--existing-customer · --walk-in-new-customer ·
// --slot-taken · --staff-pick · created).
//
// One panel, steps in place: {customer} → {service} → [{staff}] → الوقت →
// coral «تأكيد الموعد» (existing customer) / «إضافة» (new walk-in card).
// The panel owns the screen's only coral while open; the top-bar «موعد جديد»
// stays neutral (shell). Writes: POST /v1/business/bookings with an
// Idempotency-Key; 409 slot_taken stays IN the panel (red slot + server
// sentence verbatim + disabled coral); success closes the panel with the calm
// toast «تم الحجز. {name} · {when}» + «عرض».
// Offline: the panel opens and reads, the coral is disabled (appointments
// never queue — decision 12).

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Search } from 'lucide-react';
import { useErp } from '@/app/erp-context';
import { Avatar, Chip, EmptyState, ErrorCard, FILTERED_EMPTY, Field, OfflineStrip, OptionRow, PrimaryAction, SidePanel, Skeleton, TextInput, useOnline, useToast } from '@/components';
import { isApiError } from '@/lib/api';
import { formatIraqiPhone, normalizeDigits, normalizeIraqiPhone } from '@/lib/digits';
import { arNumber, baghdadToday, formatDate, formatMoney, formatTime } from '@/lib/format';
import { useLayout } from '@/lib/layout';
import { bookApi, useCustomerBook, useCustomerSearch, useEffective, useServices, useSlots } from '../data/book-api';
import type { Appointment, CreateBookingBody, CustomerItem, ServiceItem, Slot, StaffBrief } from '../data/types';
import { NetworkCard } from '../components/BookCards';
import { addDays, baghdadDateOf, nowMinuteIso, plusMinutes, shortWhen, SLOT_GROUP_LABEL, slotGroupOf, summaryWhen, wallClock, weekdayDay, type SlotGroup } from '../lib/time';
import { useDebounced } from '../lib/use-debounced';
import { countLabel, staffNounFor, wordsFor, type Words } from '../lib/words';
import { useGlobalSearchFeed } from './global-search';
import { keyForBody, type IdempotencySlot } from './idempotency';
import {
  canConfirm,
  currentStep,
  initialState,
  newCustomerErrors,
  reducer,
  sourceFor,
  stepDone,
  stepsFor,
  type PanelState,
  type Prefill,
  type StepKey,
} from './state';
import '../booking.css';

export interface BookingPrefill extends Prefill {
  /** Resolve this customer from the book (deep link `?customer=<id>`). */
  customerId?: string | null;
}

export interface BookingPanelProps {
  open: boolean;
  onClose: () => void;
  prefill?: BookingPrefill;
  onCreated?: (appointment: Appointment) => void;
  /** Listen to the top-bar search while open (default true). */
  feedGlobalSearch?: boolean;
  /** Search debounce (tests pass 0). */
  debounceMs?: number;
}

export function BookingPanel(props: BookingPanelProps) {
  if (!props.open) return null;
  return <OpenBookingPanel {...props} />;
}

/** Display name as the boards write it: «فاطمة العامري (أم محمد)». */
export function customerDisplayName(c: Pick<CustomerItem, 'name' | 'alias'>): string {
  return c.alias && c.alias !== c.name ? `${c.name} (${c.alias})` : c.name;
}

/** «· ٣ زيارات · آخر زيارة ٢ أيلول» (counts are API figures). */
export function customerFacts(c: CustomerItem, w: Words, withLast = true): string {
  const parts: string[] = [];
  parts.push(c.visits > 0 ? countLabel(c.visits, w.appointments) : w.firstAppointment);
  if (withLast && c.last_visit_at) parts.push(`${w.lastAppointment} ${formatDate(c.last_visit_at)}`);
  return parts.join(' · ');
}

function isPhoneQuery(q: string): boolean {
  return normalizeIraqiPhone(q) != null;
}

/** A number being typed («0750 222»): digits only, at least 7 of them. */
function isNumberQuery(q: string): boolean {
  const d = normalizeDigits(q);
  return d.length >= 7 && d.length === q.replace(/[\s-]/g, '').length;
}

function OpenBookingPanel({ onClose, prefill = {}, onCreated, feedGlobalSearch = true, debounceMs = 250 }: BookingPanelProps) {
  const { termProfile, t, me } = useErp();
  const words = wordsFor(termProfile);
  const staffNoun = staffNounFor(termProfile, me.business.sector);
  const online = useOnline();
  const layout = useLayout();
  const phone = layout === 'phone';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const today = useMemo(() => baghdadToday(), []);
  const [s, dispatch] = useReducer(reducer, undefined, () => initialState(today, prefill));
  /** The key of the last body sent; reused only for that exact body (./idempotency). */
  const idem = useRef<IdempotencySlot | null>(null);
  const [otherOpen, setOtherOpen] = useState(s.when.kind === 'day' && s.when.chip === 'other');

  // ---- customer step ------------------------------------------------------
  const setQuery = useCallback((v: string) => dispatch({ type: 'query', value: v }), []);
  useGlobalSearchFeed(feedGlobalSearch, setQuery);
  const q = useDebounced(s.query.trim(), debounceMs);
  const search = useCustomerSearch(q, !s.customer || s.customer.kind === 'new');
  const book = useCustomerBook(!!prefill.customerId && !s.customer);
  const results = search.data?.customers;

  // `?customer=<id>` → resolve from the book once.
  useEffect(() => {
    if (!prefill.customerId || s.customer) return;
    const found = book.data?.customers.find((c) => c.id === prefill.customerId);
    if (found) dispatch({ type: 'pickCustomer', customer: found });
  }, [book.data, prefill.customerId, s.customer]);

  // Search outcome drives the step: one phone match = that customer (board
  // caption); zero matches = the new-customer card, following the search text
  // until the person edits it.
  useEffect(() => {
    if (!results || q !== s.query.trim() || !q) return;
    const c = s.customer;
    if (c?.kind === 'existing') return;
    // A family member's row carries the BOOKER's phone (bff CustomerFamily), so
    // a number identifies the one record that owns it.
    const owners = results.filter((x) => !x.family);
    if (owners.length === 1 && isNumberQuery(q) && !c) {
      dispatch({ type: 'pickCustomer', customer: owners[0] });
      return;
    }
    if (results.length === 0) {
      if (!c || (c.kind === 'new' && !c.touched)) {
        const asPhone = isPhoneQuery(q);
        const name = asPhone ? '' : q;
        const ph = asPhone ? q : '';
        if (!c || c.name !== name || c.phone !== ph) dispatch({ type: 'startNew', name, phone: ph });
      }
    } else if (c?.kind === 'new' && !c.touched) {
      dispatch({ type: 'changeCustomer' });
    }
  }, [results, q, s.query, s.customer]);

  // ---- service / staff / time -------------------------------------------
  const services = useServices();
  const enabledServices = useMemo(() => (services.data?.services ?? []).filter((x) => x.enabled), [services.data]);
  const service = enabledServices.find((x) => x.id === s.serviceId) ?? null;
  const effective = useEffective(s.serviceId);
  const staffPick = effective.data?.staff_pick === true;
  const steps = stepsFor(staffPick);
  const current = currentStep(s, steps);
  const slotDate = s.when.kind === 'day' ? s.when.date : null;
  const slots = useSlots(s.serviceId ? slotDate : null, s.serviceId, s.staffId);

  // `?staff_id=` → keep that column's staff member only if the service's
  // eligible staff (the API's list) includes them; otherwise «تلقائي».
  useEffect(() => {
    if (!s.wantStaff || s.staffId !== s.wantStaff || !effective.data || effective.isPlaceholderData) return;
    if (!effective.data.eligible_staff.some((x) => x.id === s.wantStaff)) dispatch({ type: 'wantStaffIneligible' });
  }, [s.wantStaff, s.staffId, effective.data, effective.isPlaceholderData]);

  // `?time=HH:MM` (gap «احجز هنا») → preselect that slot if it is offered.
  useEffect(() => {
    if (!s.wantTime || !slots.data) return;
    const hit = slots.data.slots.find((x) => wallClock(x.start) === s.wantTime);
    if (hit) dispatch({ type: 'pickSlot', slot: hit });
    else dispatch({ type: 'wantTimeResolved' });
  }, [s.wantTime, slots.data]);

  // ---- confirm ------------------------------------------------------------
  const nowStart = s.when.kind === 'now' ? nowMinuteIso() : null;
  const start = s.when.kind === 'now' ? nowStart : s.slot?.start ?? null;
  const end = s.when.kind === 'now' ? (service && nowStart ? plusMinutes(nowStart, service.duration_min) : null) : s.slot?.end ?? null;
  const ready = canConfirm(s, online);
  const errs = newCustomerErrors(s.customer);
  const isNew = s.customer?.kind === 'new';

  const submit = async () => {
    if (!s.customer || !service || !start) return;
    if (errs.name || errs.phone) {
      dispatch({ type: 'attempt' });
      return;
    }
    dispatch({ type: 'submit' });
    const body: CreateBookingBody =
      s.customer.kind === 'existing'
        ? {
            service_id: service.id,
            start_at: start,
            customer_name: s.customer.customer.name,
            customer_phone: s.customer.customer.phone || undefined,
            customer_id: s.customer.customer.id,
            source: sourceFor(s),
          }
        : {
            service_id: service.id,
            start_at: start,
            customer_name: s.customer.name.trim(),
            customer_phone: s.customer.phone.trim() ? (normalizeIraqiPhone(s.customer.phone) as string) : undefined,
            source: sourceFor(s),
          };
    if (s.staffId) body.staff_id = s.staffId;
    // Same body as a failed (network) attempt → same key; anything changed → a new key.
    const attempt = keyForBody(idem.current, body);
    idem.current = attempt;
    try {
      const appt = await bookApi.createBooking(body, attempt.key);
      idem.current = null;
      void qc.invalidateQueries({ queryKey: ['business'] });
      const date = baghdadDateOf(appt.start_at);
      toast(`تم الحجز. ${appt.customer_name} · ${shortWhen(appt.start_at)}`, {
        duration: 5000,
        action: { label: 'عرض', onClick: () => navigate(`/today?date=${date}&appointment=${encodeURIComponent(appt.id)}`) },
      });
      onCreated?.(appt);
      onClose();
    } catch (e) {
      const network = isApiError(e) && e.isNetwork;
      if (!network) idem.current = null; // a refused write is a new attempt next time
      if (isApiError(e) && e.status === 409 && e.code === 'slot_taken') {
        dispatch({ type: 'slotTaken', start, detail: e.detail || 'هذا الموعد محجوز. اختر وقتاً آخر.' });
        void slots.refetch();
        return;
      }
      dispatch({ type: 'failed', error: e });
    }
  };

  const customerLabel = s.customer?.kind === 'existing' ? s.customer.customer.name : s.customer?.kind === 'new' ? s.customer.name.trim() : '';
  const summary =
    s.conflict != null
      ? 'اختر وقتاً متاحاً للمتابعة'
      : customerLabel && service && start && end
        ? `${customerLabel} · ${service.name} ${countDur(service.duration_min)} · ${summaryWhen(start, end, s.when.kind === 'now')} · ${formatMoney(service.price_minor)}`
        : null;

  const footer = (
    <div className="bk-footer">
      {summary && (
        <p className="bk-summary" data-testid="booking-summary" data-tone={s.conflict ? 'warn' : undefined}>
          {summary}
        </p>
      )}
      <PrimaryAction size="lg" block disabled={!ready} loading={s.submitting} onClick={() => void submit()} data-testid="booking-confirm">
        {isNew ? 'إضافة' : 'تأكيد الموعد'}
      </PrimaryAction>
    </div>
  );

  const stepNo = (k: StepKey) => steps.indexOf(k) + 1;
  const stepState = (k: StepKey): StepBadgeState => (k === current ? 'current' : stepDone(s, k) ? 'done' : 'todo');

  return (
    <SidePanel open title="موعد جديد" onClose={onClose} footer={footer} data-testid="booking-panel">
      <div className="bk-panel" data-layout={layout}>
        <OfflineStrip />

        {/* ---- 1 · customer ---- */}
        <section className="bk-step" aria-labelledby="bk-step-customer" data-step="customer" data-state={stepState('customer')}>
          <StepHead id="bk-step-customer" n={stepNo('customer')} state={stepState('customer')} label={t('customer')} />
          {s.customer?.kind === 'existing' ? (
            <CustomerCard customer={s.customer.customer} words={words} onChange={() => dispatch({ type: 'clearCustomer' })} />
          ) : (
            <>
              <div className="bk-search">
                <TextInput
                  icon={Search}
                  value={s.query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ابحث باسم أو رقم هاتف"
                  aria-label={`ابحث عن ${t('customer')} باسم أو رقم هاتف`}
                  data-testid="booking-search"
                />
                {q && results && results.length === 0 && (
                  <span className="bk-hint" data-testid="booking-no-results">
                    لا نتائج لـ«{q}» — أضفه {words.customerAcc} جديداً
                  </span>
                )}
              </div>
              {search.isError && <NetworkCard compact onRetry={() => void search.refetch()} />}
              {search.isFetching && !results && <ResultsSkeleton />}
              {results && results.length > 0 && !isNew && (
                <ul className="bk-results" aria-label={`نتائج البحث عن ${t('customer')}`}>
                  {results.slice(0, 6).map((c) => (
                    <li key={c.id}>
                      <button type="button" className="bk-result" onClick={() => dispatch({ type: 'pickCustomer', customer: c })}>
                        <Avatar name={c.name} size={36} />
                        <span className="bk-result-text">
                          <span className="bk-name">{customerDisplayName(c)}</span>
                          <span className="bk-sub">
                            {c.phone && (
                              <>
                                <span className="ltr">{formatIraqiPhone(c.phone)}</span>{' '}
                              </>
                            )}
                            · {customerFacts(c, words, false)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {s.customer?.kind === 'new' && (
                <div className="bk-newcard" data-testid="booking-new-customer">
                  <div className="bk-newcard-title">{words.customerIndef} جديد</div>
                  <Field label="الاسم" error={s.showErrors && errs.name ? `اسم ${t('customer')} مطلوب` : undefined}>
                    {(id) => (
                      <TextInput id={id} value={s.customer?.kind === 'new' ? s.customer.name : ''} invalid={s.showErrors && !!errs.name} onChange={(e) => dispatch({ type: 'editNew', name: e.target.value })} />
                    )}
                  </Field>
                  <Field label="الهاتف (اختياري)" error={s.showErrors && errs.phone ? 'الهاتف بصيغة 07XX XXX XXXX' : undefined}>
                    {(id) => (
                      <TextInput
                        id={id}
                        ltr
                        inputMode="tel"
                        placeholder="07XX XXX XXXX"
                        value={s.customer?.kind === 'new' ? s.customer.phone : ''}
                        invalid={s.showErrors && !!errs.phone}
                        onChange={(e) => dispatch({ type: 'editNew', phone: e.target.value })}
                      />
                    )}
                  </Field>
                </div>
              )}
            </>
          )}
        </section>

        {/* ---- 2 · service ---- */}
        <section className="bk-step" aria-labelledby="bk-step-service" data-step="service" data-state={stepState('service')}>
          <StepHead id="bk-step-service" n={stepNo('service')} state={stepState('service')} label={words.serviceStep} />
          {services.isLoading ? (
            <OptionsSkeleton />
          ) : services.isError ? (
            <NetworkCard compact onRetry={() => void services.refetch()} />
          ) : enabledServices.length === 0 ? (
            <EmptyState filtered title="لا خدمات مفعّلة." data-testid="booking-no-services" />
          ) : phone && service ? (
            <CollapsedRow text={`${service.name} · ${serviceFacts(service)}`} onChange={() => dispatch({ type: 'changeService' })} />
          ) : (
            <div className="bk-options" role="radiogroup" aria-label={words.serviceStep}>
              {enabledServices.map((x) => (
                <OptionRow key={x.id} selected={x.id === s.serviceId} onSelect={() => dispatch({ type: 'pickService', serviceId: x.id })} title={x.name} sub={serviceFacts(x)} />
              ))}
            </div>
          )}
        </section>

        {/* ---- 3 · staff (only when the server allows a pick) ---- */}
        {staffPick && effective.data && (
          <section className="bk-step" aria-labelledby="bk-step-staff" data-step="staff" data-state={stepState('staff')} data-testid="booking-staff-step">
            <StepHead id="bk-step-staff" n={stepNo('staff')} state={stepState('staff')} label={staffNoun.label} />
            <StaffPicker staff={effective.data.eligible_staff} value={s.staffId} feminine={staffNoun.feminine} label={staffNoun.label} onChange={(id) => dispatch({ type: 'pickStaff', staffId: id })} />
          </section>
        )}

        {/* ---- time ---- */}
        <section className="bk-step" aria-labelledby="bk-step-time" data-step="time" data-state={stepState('time')}>
          <StepHead id="bk-step-time" n={stepNo('time')} state={stepState('time')} label="الوقت" />
          <div className="bk-days" role="group" aria-label="اليوم">
            <Chip selected={s.when.kind === 'day' && s.when.chip === 'today'} onClick={() => { setOtherOpen(false); dispatch({ type: 'pickDay', date: today, chip: 'today' }); }}>
              اليوم
            </Chip>
            <Chip selected={s.when.kind === 'day' && s.when.chip === 'tomorrow'} onClick={() => { setOtherOpen(false); dispatch({ type: 'pickDay', date: addDays(today, 1), chip: 'tomorrow' }); }}>
              {s.when.kind === 'day' && s.when.chip === 'tomorrow' ? `غداً · ${weekdayDay(s.when.date)}` : 'غداً'}
            </Chip>
            <Chip selected={s.when.kind === 'day' && s.when.chip === 'other'} onClick={() => setOtherOpen(true)}>
              {s.when.kind === 'day' && s.when.chip === 'other' ? formatDate(s.when.date, { weekday: true }) : 'تاريخ آخر'}
            </Chip>
            <Chip selected={s.when.kind === 'now'} onClick={() => { setOtherOpen(false); dispatch({ type: 'pickNow' }); }}>
              {s.when.kind === 'now' ? `الآن · ${formatTime(nowStart ?? new Date())}` : 'الآن'}
            </Chip>
          </div>
          {otherOpen && (
            <Field label="تاريخ آخر">
              {(id) => (
                <TextInput
                  id={id}
                  type="date"
                  ltr
                  min={today}
                  value={s.when.kind === 'day' && s.when.chip === 'other' ? s.when.date : ''}
                  onChange={(e) => e.target.value && dispatch({ type: 'pickDay', date: e.target.value, chip: e.target.value === today ? 'today' : 'other' })}
                  data-testid="booking-other-date"
                />
              )}
            </Field>
          )}
          {s.when.kind === 'day' && (
            <SlotsArea
              hasService={!!s.serviceId}
              loading={slots.isLoading}
              error={slots.error}
              slots={slots.data?.slots}
              state={s}
              onPick={(slot) => dispatch({ type: 'pickSlot', slot })}
              onRetry={() => void slots.refetch()}
            />
          )}
          {s.conflict && (
            <div className="bk-conflict" role="alert" data-testid="booking-conflict">
              {s.conflict.detail}
            </div>
          )}
          {s.error != null && <SubmitError error={s.error} onRetry={() => void submit()} onRefresh={() => void Promise.all([slots.refetch(), services.refetch()])} />}
        </section>
      </div>
    </SidePanel>
  );
}

// ------------------------------------------------------------ pieces --

function countDur(min: number): string {
  return `${formatDurationNumber(min)} د`;
}

function formatDurationNumber(min: number): string {
  return arNumber(min);
}

/** «٤٥ د · ٥٠٬٠٠٠ د.ع». */
export function serviceFacts(x: Pick<ServiceItem, 'duration_min' | 'price_minor'>): string {
  return `${countDur(x.duration_min)} · ${formatMoney(x.price_minor)}`;
}

type StepBadgeState = 'done' | 'current' | 'todo';

function StepHead({ id, n, state, label }: { id: string; n: number; state: StepBadgeState; label: string }) {
  return (
    <div className="bk-stephead">
      <span className="bk-badge" data-state={state} aria-hidden="true">
        {state === 'done' ? <Check /> : formatDurationNumber(n)}
      </span>
      <h3 id={id} className="bk-steplabel">
        {label}
      </h3>
    </div>
  );
}

function CustomerCard({ customer, words, onChange }: { customer: CustomerItem; words: Words; onChange: () => void }) {
  return (
    <div className="bk-custcard" data-testid="booking-customer-card">
      <Avatar name={customer.name} size={36} />
      <span className="bk-result-text">
        <span className="bk-name">{customerDisplayName(customer)}</span>
        <span className="bk-sub">
          {customer.phone && (
            <>
              <span className="ltr">{formatIraqiPhone(customer.phone)}</span>{' '}
            </>
          )}
          · {customerFacts(customer, words)}
        </span>
      </span>
      <button type="button" className="bk-link" onClick={onChange}>
        تغيير
      </button>
    </div>
  );
}

function CollapsedRow({ text, onChange }: { text: string; onChange: () => void }) {
  return (
    <div className="bk-collapsed">
      <span>{text}</span>
      <button type="button" className="bk-link" onClick={onChange}>
        تغيير
      </button>
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="bk-results" aria-busy="true">
      {[0, 1].map((i) => (
        <Skeleton key={i} height={52} radius={14} />
      ))}
    </div>
  );
}

function OptionsSkeleton() {
  return (
    <div className="bk-options" aria-busy="true" data-testid="booking-services-loading">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} height={56} radius={14} />
      ))}
    </div>
  );
}

/** Staff step: «تلقائي» first, then the eligible staff — available first. */
function StaffPicker({ staff, value, feminine, label, onChange }: { staff: StaffBrief[]; value: string; feminine: boolean; label: string; onChange: (id: string) => void }) {
  const ordered = [...staff].sort((a, b) => Number(b.available_now) - Number(a.available_now));
  const busy = ordered.filter((x) => !x.available_now && !x.off_today && x.busy_until);
  return (
    <div className="bk-staff">
      <div className="bk-staff-row" role="radiogroup" aria-label={label}>
        <button type="button" role="radio" aria-checked={value === ''} className="bk-staff-auto" onClick={() => onChange('')}>
          تلقائي
        </button>
        {ordered.map((x) => (
          <button
            key={x.id}
            type="button"
            role="radio"
            aria-checked={value === x.id}
            className="bk-staff-opt"
            data-available={x.available_now ? 'true' : 'false'}
            disabled={x.off_today}
            onClick={() => onChange(x.id)}
          >
            <Avatar name={x.full_name} size={36} />
            <span>{x.full_name}</span>
          </button>
        ))}
      </div>
      <p className="bk-hint" data-testid="booking-staff-hint">
        {['المتاحات أولاً', ...busy.map((x) => `${x.full_name} ${feminine ? 'مشغولة' : 'مشغول'} حتى ${formatTime(x.busy_until as string)}`)].join(' · ')}
      </p>
    </div>
  );
}

interface SlotsAreaProps {
  hasService: boolean;
  loading: boolean;
  error: unknown;
  slots: Slot[] | undefined;
  state: PanelState;
  onPick: (s: Slot) => void;
  onRetry: () => void;
}

const GROUP_ORDER: SlotGroup[] = ['morning', 'afternoon', 'evening'];

function SlotsArea({ hasService, loading, error, slots, state, onPick, onRetry }: SlotsAreaProps) {
  if (!hasService) return null;
  if (loading) {
    return (
      <div className="bk-slots" aria-busy="true" data-testid="booking-slots-loading">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} width={64} height={34} radius={999} />
        ))}
      </div>
    );
  }
  if (error) {
    // 409 service_inactive (a switched-off service) and other refusals carry
    // the server's Arabic sentence; anything else reads as a network failure.
    if (isApiError(error) && !error.isNetwork) return <ErrorCard kind="conflict" error={error} onRefresh={onRetry} />;
    return <NetworkCard compact onRetry={onRetry} />;
  }
  // The API returns only free slots; a time the server just refused stays
  // drawn (red while it is the live conflict, then struck).
  const known = new Map<string, Slot>();
  for (const x of slots ?? []) known.set(x.start, x);
  for (const start of state.taken) if (!known.has(start)) known.set(start, { start, end: start });
  const list = [...known.values()].sort((a, b) => a.start.localeCompare(b.start));
  if (list.length === 0) return <EmptyState filtered title={FILTERED_EMPTY} data-testid="booking-no-slots" />;
  const groups = GROUP_ORDER.map((g) => ({ g, items: list.filter((x) => slotGroupOf(x.start) === g) })).filter((x) => x.items.length > 0);
  return (
    <div className="bk-slotgroups" data-testid="booking-slots">
      {groups.map(({ g, items }) => (
        <div key={g} className="bk-slotgroup" role="group" aria-label={SLOT_GROUP_LABEL[g]}>
          <div className="bk-grouplabel">{SLOT_GROUP_LABEL[g]}</div>
          <div className="bk-slots">
            {items.map((x) => {
              const conflict = state.conflict?.start === x.start;
              const taken = !conflict && state.taken.includes(x.start);
              const selected = state.slot?.start === x.start;
              const st = conflict ? 'conflict' : taken ? 'taken' : selected ? 'selected' : 'free';
              return (
                <button
                  key={x.start}
                  type="button"
                  className="bk-slot"
                  data-state={st}
                  aria-pressed={selected}
                  disabled={taken || conflict}
                  onClick={() => onPick(x)}
                >
                  {formatTime(x.start, { suffix: false })}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function SubmitError({ error, onRetry, onRefresh }: { error: unknown; onRetry: () => void; onRefresh: () => void }) {
  if (isApiError(error) && !error.isNetwork) return <ErrorCard kind="conflict" error={error} onRefresh={onRefresh} />;
  return <NetworkCard compact onRetry={onRetry} />;
}
